import { FC, MouseEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { darken } from '../../utils/polished';
import { buttonReset } from '../../utils/style-utils';
import Stat, { IStatProps } from './Stat';

const StatButtonElement = styled.div<Pick<IProps, 'onClick' | 'active'>>`
  ${buttonReset};
  background-color: ${({ active, theme }) => (active ? theme.vars.newColor.lightGS2 : 'transparent')};
  border-bottom: 3px solid ${({ active, theme }) => (active ? theme.vars.newColor.blue1 : 'transparent')};
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  display: inline-flex;

  &:focus,
  &:hover {
    background-color: ${props => getHoverBgColor(props)};
  }

  &:active {
    background-color: ${props => darken(0.05, getHoverBgColor(props))};
  }
`;

export interface IProps extends IStatProps {
  /** Button ARIA label tag. If not provided uses the name. */
  ariaLabel?: string;
  /** Enables active styling. */
  active?: boolean;
  /** Event that is invoked when the stat component is clicked. */
  onClick?: (e: MouseEvent | KeyboardEvent) => void;
}

export const StatButton: FC<IProps> = ({ active, ariaLabel, name, onClick, ...statProps }) => {
  const handleClick = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (onClick) {
      onClick(e);
    }
  };

  return (
    <StatButtonElement
      data-testid="StatButton"
      active={active}
      onClick={onClick ? handleClick : null}
      as={onClick ? 'button' : null}
      aria-label={ariaLabel ? ariaLabel : name}
    >
      <Stat name={name} {...statProps} variation="standard" mode="light" />
    </StatButtonElement>
  );
};

function getHoverBgColor(props) {
  if (!props.onClick) {
    return 'auto';
  }
  return props.active ? darken(0.02, props.theme.vars.newColor.lightGS2) : props.theme.vars.newColor.lightGS2;
}

export default StatButton;
